import '../styles/NewAthletePage.css';

import React, {
  useEffect,
  useState,
} from 'react';

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import throttle from 'lodash.throttle';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import firebase from '../firebase';
import { Athlete } from '../types/types';
import { checkSessionCodeAndLogoutIfChanged } from '../utils/sessionUtils';
import {
  Button,
  Card,
  CommentsBox,
  Container,
  Input,
  PhotoUpload,
  SkillJudge,
  Typography,
} from './common';
import NewAthleteTabs from './NewAthleteTabs';

interface Coach {
  id: string;
  name: string;
  joinedAt: any;
}

type DetailSection = 'main' | 'top-flexibility' | 'jumps' | 'tumbling' | 'stunt' | 'basket' | 'dance' | 'comments';

export default function NewAthletePage() {
  const { sessionId, athleteId } = useParams<{ sessionId: string; athleteId: string }>();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [currentAthlete, setCurrentAthlete] = useState<Athlete | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [activeSection, setActiveSection] = useState<DetailSection>('main');
  const [topFlexComments, setTopFlexComments] = useState('');
  const [jumpComments, setJumpComments] = useState('');
  const [stuntComments, setStuntComments] = useState('');
  const [basketComments, setBasketComments] = useState('');
  const [danceComments, setDanceComments] = useState('');
  const [tumbleComments, setTumbleComments] = useState('');
  const [commentList, setCommentList] = useState<string[]>(['']);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [fullImageLoaded, setFullImageLoaded] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editing, setEditing] = useState(false);
  const [previousCoach, setPreviousCoach] = useState<string | undefined>(undefined);
  const [editForm, setEditForm] = useState({
    name: '',
    number: '',
    position: '',
    returningstatus: '',
    createdBy: '',
    imageUrl: '',
    imageThumbnailUrl: ''
  });
  const [showStickyName, setShowStickyName] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const navigate = useNavigate();
  const db = getFirestore(firebase);
  const coachName = localStorage.getItem('coachName') || '';

  // Initialize data fetching
  const initializeData = async () => {
    if (!sessionId) {
      setError('Session ID is missing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Fetch all coaches from the session
      const coachesRef = collection(db, `sessions/${sessionId}/coaches`);
      const coachesSnapshot = await getDocs(coachesRef);
      const coachesList: Coach[] = [];
      coachesSnapshot.forEach(doc => {
        coachesList.push({
          id: doc.id,
          ...doc.data()
        } as Coach);
      });

      // Sort coaches by name
      coachesList.sort((a, b) => a.name.localeCompare(b.name));
      setCoaches(coachesList);

      // Fetch athletes assigned to this coach
      const athletesRef = collection(db, `sessions/${sessionId}/athletes`);
      const q = query(
        athletesRef,
        where('createdBy', '==', coachName)
      );
      const querySnapshot = await getDocs(q);

      const athletesList: Athlete[] = [];
      querySnapshot.forEach(doc => {
        athletesList.push({
          id: doc.id,
          ...doc.data()
        } as Athlete);
      });

      setAthletes(athletesList);

      // If athleteId is provided, set it as current athlete
      if (athleteId) {
        const athleteDoc = await getDoc(doc(db, `sessions/${sessionId}/athletes/${athleteId}`));
        if (athleteDoc.exists()) {
          const athleteData = athleteDoc.data() as Omit<Athlete, 'id'>;
          const newAthlete = { id: athleteDoc.id, ...athleteData };
          setCurrentAthlete(newAthlete);
          initializeAthleteState(newAthlete);
        }
      } else if (athletesList.length > 0) {
        // If no athleteId provided, set first athlete as current
        setCurrentAthlete(athletesList[0]);
        initializeAthleteState(athletesList[0]);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load athlete data');
    } finally {
      setLoading(false);
    }
  };

  const initializeAthleteState = (athlete: Athlete) => {
    // Reset all comment states
    setTopFlexComments(athlete.skills?.topflexcomments || '');
    setJumpComments(athlete.skills?.jumpcomments || '');
    setStuntComments(athlete.skills?.stuntcomments || '');
    setBasketComments(athlete.skills?.basketcomments || '');
    setDanceComments(athlete.skills?.dancecomments || '');
    setTumbleComments(athlete.skills?.tumblecomments || '');
    setCommentList(athlete.commentList || ['']);

    // Set edit form
    setEditForm({
      name: athlete.name,
      number: athlete.number,
      position: athlete.position,
      returningstatus: athlete.returningstatus || 'New',
      createdBy: athlete.createdBy,
      imageUrl: athlete.imageUrl,
      imageThumbnailUrl: athlete.imageThumbnailUrl || ''
    });
  };

  // Call initializeData when component mounts
  useEffect(() => {
    checkSessionCodeAndLogoutIfChanged(localStorage.getItem('sessionId')!);
    initializeData();
  }, [sessionId]);

  // Update current athlete when athleteId changes
  useEffect(() => {
    if (athleteId && athletes.length > 0) {
      const athlete = athletes.find(a => a.id === athleteId);
      if (athlete) {
        setCurrentAthlete(athlete);
        initializeAthleteState(athlete);
      }
    }
  }, [athleteId, athletes]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      setShowStickyName(window.scrollY > 100);
    }, 200);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAthleteSelect = (athlete: Athlete) => {
    setCurrentAthlete(athlete);
    initializeAthleteState(athlete);
    navigate(`/session/${sessionId}/athlete-page/${athlete.id}`, { replace: true });
  };

  const handleRefresh = () => {
    initializeData();
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleFullImageLoad = () => {
    setFullImageLoaded(true);
  };

  const handleDialogVisibility = (show: boolean) => {
    document.body.style.overflow = show ? 'hidden' : '';
  };

  const handleShowEditDialog = () => {
    setShowEditDialog(true);
    handleDialogVisibility(true);
  };

  const handleHideEditDialog = () => {
    setShowEditDialog(false);
    handleDialogVisibility(false);
  };

  const handleShowDeleteDialog = () => {
    setShowDeleteDialog(true);
    handleDialogVisibility(true);
  };

  const handleHideDeleteDialog = () => {
    setShowDeleteDialog(false);
    handleDialogVisibility(false);
  };

  const handleCoachChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCoach = e.target.value;

    if (!currentAthlete) return;

    try {
      setPreviousCoach(currentAthlete.createdBy);
      await updateDoc(doc(db, `sessions/${sessionId}/athletes/${currentAthlete.id}`), {
        createdBy: newCoach
      });
      setCurrentAthlete(prev => prev ? { ...prev, createdBy: newCoach } : prev);
      setAthletes(prevAthletes => {
        if (newCoach === coachName) {
          // Add to athletes if not already present
          if (!prevAthletes.some(a => a.id === currentAthlete.id)) {
            return [...prevAthletes, { ...currentAthlete, createdBy: newCoach }];
          } else {
            return prevAthletes.map(a => a.id === currentAthlete.id ? { ...a, createdBy: newCoach } : a);
          }
        } else {
          // Remove from athletes if present
          return prevAthletes.filter(a => a.id !== currentAthlete.id);
        }
      });
    } catch (error) {
      console.error('Error updating coach:', error);
      setError('Failed to update coach assignment');
    }
  };

  const handleUndoCoachChange = async () => {
    if (!currentAthlete || previousCoach === undefined) return;

    try {
      setPreviousCoach(undefined); // Hide Undo button immediately
      await updateDoc(doc(db, `sessions/${sessionId}/athletes/${currentAthlete.id}`), {
        createdBy: previousCoach
      });
      setCurrentAthlete(prev => prev ? { ...prev, createdBy: previousCoach } : prev);
      setAthletes(prevAthletes => {
        if (previousCoach === coachName) {
          // Add to athletes if not already present
          if (!prevAthletes.some(a => a.id === currentAthlete.id)) {
            return [...prevAthletes, { ...currentAthlete, createdBy: previousCoach }];
          } else {
            return prevAthletes.map(a => a.id === currentAthlete.id ? { ...a, createdBy: previousCoach } : a);
          }
        } else {
          // Remove from athletes if present
          return prevAthletes.filter(a => a.id !== currentAthlete.id);
        }
      });
    } catch (error) {
      console.error('Error updating coach:', error);
      setError('Failed to update coach assignment');
    }
  };

  const handleDeleteAthlete = async () => {
    if (!currentAthlete) return;

    try {
      setDeleting(true);
      await deleteDoc(doc(db, `sessions/${sessionId}/athletes/${currentAthlete.id}`));
      // Remove from athletes list
      setAthletes(athletes.filter(a => a.id !== currentAthlete.id));
      // If there are more athletes, select the first one
      if (athletes.length > 1) {
        const nextAthlete = athletes.find(a => a.id !== currentAthlete.id);
        if (nextAthlete) {
          handleAthleteSelect(nextAthlete);
        }
      } else {
        navigate(`/session/${sessionId}/athletes`);
      }
    } catch (error) {
      console.error('Error deleting athlete:', error);
      setError('Failed to delete athlete');
    } finally {
      setDeleting(false);
      handleHideDeleteDialog();
    }
  };

  const updateSkillValue = async (skillName: string, value: number) => {
    if (!currentAthlete || !sessionId) return;

    try {
      const skills = currentAthlete.skills || {};
      const updatedSkills = {
        ...skills,
        [skillName]: value
      };

      await updateDoc(doc(db, `sessions/${sessionId}/athletes/${currentAthlete.id}`), {
        skills: updatedSkills
      });

      // Update both current athlete and the athletes list
      const updatedAthlete = {
        ...currentAthlete,
        skills: updatedSkills
      };
      setCurrentAthlete(updatedAthlete);
      setAthletes(athletes.map(a => a.id === currentAthlete.id ? updatedAthlete : a));
    } catch (error) {
      console.error(`Error updating ${skillName}:`, error);
      setError(`Failed to update ${skillName}`);
    }
  };

  const updateSkillImage = async (skillName: string, imageUrl: string) => {
    if (!currentAthlete || !sessionId) return;

    try {
      const skills = currentAthlete.skills || {};
      const updatedSkills = {
        ...skills,
        [skillName]: imageUrl
      };

      await updateDoc(doc(db, `sessions/${sessionId}/athletes/${currentAthlete.id}`), {
        skills: updatedSkills
      });

      // Update both current athlete and the athletes list
      const updatedAthlete = {
        ...currentAthlete,
        skills: updatedSkills
      };
      setCurrentAthlete(updatedAthlete);
      setAthletes(athletes.map(a => a.id === currentAthlete.id ? updatedAthlete : a));
    } catch (error) {
      console.error(`Error updating ${skillName}:`, error);
      setError(`Failed to update ${skillName}`);
    }
  };

  const updateComments = async (comments: string, section: 'topflex' | 'jump' | 'stunt' | 'basket' | 'dance' | 'tumble') => {
    if (!currentAthlete || !sessionId) return;

    const field = `${section}comments`;
    const athleteRef = doc(db, `sessions/${sessionId}/athletes/${currentAthlete.id}`);

    try {
      const skills = currentAthlete.skills || {};
      const updatedSkills = {
        ...skills,
        [field]: comments
      };

      await updateDoc(athleteRef, {
        skills: updatedSkills
      });

      // Update both current athlete and the athletes list
      const updatedAthlete = {
        ...currentAthlete,
        skills: updatedSkills
      };
      setCurrentAthlete(updatedAthlete);
      setAthletes(athletes.map(a => a.id === currentAthlete.id ? updatedAthlete : a));
    } catch (error) {
      console.error('Error updating comments:', error);
      setError(`Failed to update ${section} comments`);
    }
  };

  const saveSectionComments = async (
    section: 'topflex' | 'jump' | 'stunt' | 'basket' | 'dance' | 'tumble',
    value: string,
    athleteIdAtStart?: string
  ) => {
    if (!currentAthlete) return;
    if (athleteIdAtStart && currentAthlete.id !== athleteIdAtStart) return;
    await updateComments(value, section);
  };

  const handleCommentsBlur = (
    section: 'topflex' | 'jump' | 'stunt' | 'basket' | 'dance' | 'tumble',
    athleteIdAtStart?: string
  ) => {
    switch (section) {
      case 'topflex':
        void saveSectionComments('topflex', topFlexComments, athleteIdAtStart);
        break;
      case 'jump':
        void saveSectionComments('jump', jumpComments, athleteIdAtStart);
        break;
      case 'stunt':
        void saveSectionComments('stunt', stuntComments, athleteIdAtStart);
        break;
      case 'basket':
        void saveSectionComments('basket', basketComments, athleteIdAtStart);
        break;
      case 'dance':
        void saveSectionComments('dance', danceComments, athleteIdAtStart);
        break;
      case 'tumble':
        void saveSectionComments('tumble', tumbleComments, athleteIdAtStart);
        break;
    }
  };

  const handleCommentsChange = (value: string, section: 'topflex' | 'jump' | 'stunt' | 'basket' | 'dance' | 'tumble') => {
    switch (section) {
      case 'topflex':
        setTopFlexComments(value);
        break;
      case 'jump':
        setJumpComments(value);
        break;
      case 'stunt':
        setStuntComments(value);
        break;
      case 'basket':
        setBasketComments(value);
        break;
      case 'dance':
        setDanceComments(value);
        break;
      case 'tumble':
        setTumbleComments(value);
        break;
    }
  };

  // Also support auto-save on blur for comment list

  const updateCommentList = async (newCommentList: string[]) => {
    if (!currentAthlete || !sessionId) return;

    try {
      await updateDoc(doc(db, `sessions/${sessionId}/athletes/${currentAthlete.id}`), {
        commentList: newCommentList
      });

      // Update both current athlete and the athletes list
      const updatedAthlete = {
        ...currentAthlete,
        commentList: newCommentList
      };
      setCurrentAthlete(updatedAthlete);
      setAthletes(athletes.map(a => a.id === currentAthlete.id ? updatedAthlete : a));
    } catch (error) {
      console.error('Error updating comment list:', error);
      setError('Failed to update comments');
    }
  };

  const handleCommentChange = (index: number, value: string) => {
    const newCommentList = [...commentList];
    newCommentList[index] = value;
    setCommentList(newCommentList);
  };

  const handleCommentSave = async (index: number, athleteIdAtStart?: string) => {
    if (!currentAthlete) return;
    if (athleteIdAtStart && currentAthlete.id !== athleteIdAtStart) return;
    await updateCommentList(commentList);
  };

  const handleCommentBlur = (index: number, athleteIdAtStart?: string) => {
    void handleCommentSave(index, athleteIdAtStart);
  };

  const addNewComment = () => {
    setCommentList(['', ...commentList]);
  };

  const handleEditAthlete = async () => {
    if (!currentAthlete || !sessionId) return;

    try {
      setEditing(true);
      const updatedData = {
        name: editForm.name,
        number: editForm.number,
        position: editForm.position,
        returningstatus: editForm.returningstatus === 'Unknown' ? null : editForm.returningstatus,
        createdBy: editForm.createdBy,
        imageUrl: editForm.imageUrl || currentAthlete.imageUrl,
        imageThumbnailUrl: editForm.imageThumbnailUrl || currentAthlete.imageThumbnailUrl || ''
      };

      await updateDoc(doc(db, `sessions/${sessionId}/athletes/${currentAthlete.id}`), updatedData);

      // Update both current athlete and the athletes list
      const updatedAthlete = {
        ...currentAthlete,
        ...updatedData,
        returningstatus: editForm.returningstatus === 'Unknown' ? undefined : editForm.returningstatus,
      };
      setCurrentAthlete(updatedAthlete);
      setAthletes(athletes.map(a => a.id === currentAthlete.id ? updatedAthlete : a));
      handleHideEditDialog();
    } catch (error) {
      console.error('Error updating athlete:', error);
      setError('Failed to update athlete');
    } finally {
      setEditing(false);
    }
  };

  const renderDetailContent = () => {
    switch (activeSection) {
      case 'top-flexibility':
        return (
          <div className="detail-content">
            <SkillJudge
              title="Scale"
              value={currentAthlete?.skills?.topflexscale || 0}
              onChange={(value) => updateSkillValue('topflexscale', value)}
            />
            <PhotoUpload
              imageUrl={currentAthlete?.skills?.topflexscaleimage}
              onImageUpload={(url) => updateSkillImage('topflexscaleimage', url)}
              className="skill-photo-upload"
              withThumbnail={false}
            />

            <SkillJudge
              title="Heel Stretch"
              value={currentAthlete?.skills?.topflexheel || 0}
              onChange={(value) => updateSkillValue('topflexheel', value)}
            />
            <PhotoUpload
              imageUrl={currentAthlete?.skills?.topflexheelimage}
              onImageUpload={(url) => updateSkillImage('topflexheelimage', url)}
              className="skill-photo-upload"
              withThumbnail={false}
            />

            <SkillJudge
              title="Arabesque"
              value={currentAthlete?.skills?.topflexarab || 0}
              onChange={(value) => updateSkillValue('topflexarab', value)}
            />
            <PhotoUpload
              imageUrl={currentAthlete?.skills?.topflexarabimage}
              onImageUpload={(url) => updateSkillImage('topflexarabimage', url)}
              className="skill-photo-upload"
              withThumbnail={false}
            />

            <SkillJudge
              title="Scorpion"
              value={currentAthlete?.skills?.scorpion || 0}
              onChange={(value) => updateSkillValue('scorpion', value)}
            />
            <PhotoUpload
              imageUrl={currentAthlete?.skills?.topflexscorpionimage}
              onImageUpload={(url) => updateSkillImage('topflexscorpionimage', url)}
              className="skill-photo-upload"
              withThumbnail={false}
            />

            <SkillJudge
              title="Bow and Arrow"
              value={currentAthlete?.skills?.bowarrow || 0}
              onChange={(value) => updateSkillValue('bowarrow', value)}
            />
            <PhotoUpload
              imageUrl={currentAthlete?.skills?.topflexbowarrowimage}
              onImageUpload={(url) => updateSkillImage('topflexbowarrowimage', url)}
              className="skill-photo-upload"
              withThumbnail={false}
            />

            <CommentsBox
              value={topFlexComments}
              onChange={(value) => handleCommentsChange(value, 'topflex')}
              onSave={() => saveSectionComments('topflex', topFlexComments, currentAthlete?.id)}
              onBlur={() => handleCommentsBlur('topflex', currentAthlete?.id)}
              placeholder="Top Flexibility comments..."
            />
          </div>
        );
      case 'jumps':
        return (
          <div className="detail-content">
            <SkillJudge
              title="Toe Touch"
              value={currentAthlete?.skills?.jumptoe || 0}
              onChange={(value) => updateSkillValue('jumptoe', value)}
            />
            <SkillJudge
              title="Hurdle"
              value={currentAthlete?.skills?.jumphurdle || 0}
              onChange={(value) => updateSkillValue('jumphurdle', value)}
            />
            <SkillJudge
              title="Pike"
              value={currentAthlete?.skills?.jumppike || 0}
              onChange={(value) => updateSkillValue('jumppike', value)}
            />
            <SkillJudge
              title="Toe Tuck"
              value={currentAthlete?.skills?.jumptoetuck || 0}
              onChange={(value) => updateSkillValue('jumptoetuck', value)}
            />
            <CommentsBox
              value={jumpComments}
              onChange={(value) => handleCommentsChange(value, 'jump')}
              onSave={() => saveSectionComments('jump', jumpComments, currentAthlete?.id)}
              onBlur={() => handleCommentsBlur('jump', currentAthlete?.id)}
              placeholder="Jumps comments..."
            />
          </div>
        );
      case 'tumbling':
        return (
          <div className="detail-content">
            <div className="section-divider">
              <Typography variant="h3">Standing</Typography>
            </div>

            <SkillJudge
              title="BHS"
              value={currentAthlete?.skills?.tumblestandbhs || 0}
              onChange={(value) => updateSkillValue('tumblestandbhs', value)}
            />
            <SkillJudge
              title="Tuck"
              value={currentAthlete?.skills?.tumblestandtuck || 0}
              onChange={(value) => updateSkillValue('tumblestandtuck', value)}
            />
            <SkillJudge
              title="2x BHS Tuck"
              value={currentAthlete?.skills?.tumblestand2bhstuck || 0}
              onChange={(value) => updateSkillValue('tumblestand2bhstuck', value)}
            />
            <SkillJudge
              title="2x BHS Layout"
              value={currentAthlete?.skills?.tumblestand2bhslayout || 0}
              onChange={(value) => updateSkillValue('tumblestand2bhslayout', value)}
            />
            <SkillJudge
              title="2x BHS Full"
              value={currentAthlete?.skills?.tumblestand2bhsfull || 0}
              onChange={(value) => updateSkillValue('tumblestand2bhsfull', value)}
            />

            <div className="section-divider">
              <Typography variant="h3">Running</Typography>
            </div>

            <SkillJudge
              title="RO or RO BHS Tuck"
              value={currentAthlete?.skills?.tumblerunrotuck || 0}
              onChange={(value) => updateSkillValue('tumblerunrotuck', value)}
            />
            <SkillJudge
              title="RO or RO BHS Layout"
              value={currentAthlete?.skills?.tumblerunrolayout || 0}
              onChange={(value) => updateSkillValue('tumblerunrolayout', value)}
            />
            <SkillJudge
              title="RO or RO BHS Full"
              value={currentAthlete?.skills?.tumblerunrofull || 0}
              onChange={(value) => updateSkillValue('tumblerunrofull', value)}
            />
            <SkillJudge
              title="Combo to Layout"
              value={currentAthlete?.skills?.tumbleruncombolayout || 0}
              onChange={(value) => updateSkillValue('tumbleruncombolayout', value)}
            />
            <SkillJudge
              title="Combo to Full"
              value={currentAthlete?.skills?.tumbleruncombofull || 0}
              onChange={(value) => updateSkillValue('tumbleruncombofull', value)}
            />
            <CommentsBox
              value={tumbleComments}
              onChange={(value) => handleCommentsChange(value, 'tumble')}
              onSave={() => saveSectionComments('tumble', tumbleComments, currentAthlete?.id)}
              onBlur={() => handleCommentsBlur('tumble', currentAthlete?.id)}
              placeholder="Tumbling comments..."
            />
          </div>
        );
      case 'stunt':
        return (
          <div className="detail-content">
            <div className="section-divider">
              <Typography variant="h3">Static</Typography>
            </div>

            <SkillJudge
              title="Double Base"
              value={currentAthlete?.skills?.stuntdoublebase || 0}
              onChange={(value) => updateSkillValue('stuntdoublebase', value)}
            />
            <SkillJudge
              title="Extension"
              value={currentAthlete?.skills?.stuntextension || 0}
              onChange={(value) => updateSkillValue('stuntextension', value)}
            />
            <SkillJudge
              title="Torch"
              value={currentAthlete?.skills?.stunttouch || 0}
              onChange={(value) => updateSkillValue('stunttouch', value)}
            />
            <SkillJudge
              title="Lib"
              value={currentAthlete?.skills?.stuntlib || 0}
              onChange={(value) => updateSkillValue('stuntlib', value)}
            />
            <SkillJudge
              title="Lib w/ Air Position"
              value={currentAthlete?.skills?.stuntlibairposition || 0}
              onChange={(value) => updateSkillValue('stuntlibairposition', value)}
            />

            <div className="section-divider">
              <Typography variant="h3">Dismount</Typography>
            </div>

            <SkillJudge
              title="Cradle"
              value={currentAthlete?.skills?.stuntcradle || 0}
              onChange={(value) => updateSkillValue('stuntcradle', value)}
            />
            <SkillJudge
              title="Front Punch"
              value={currentAthlete?.skills?.stuntfrontpunch || 0}
              onChange={(value) => updateSkillValue('stuntfrontpunch', value)}
            />
            <SkillJudge
              title="Back Tuck"
              value={currentAthlete?.skills?.stuntbacktuck || 0}
              onChange={(value) => updateSkillValue('stuntbacktuck', value)}
            />
            <SkillJudge
              title="Full Down"
              value={currentAthlete?.skills?.stuntfulldown || 0}
              onChange={(value) => updateSkillValue('stuntfulldown', value)}
            />
            <SkillJudge
              title="Double Down"
              value={currentAthlete?.skills?.stuntdoubledown || 0}
              onChange={(value) => updateSkillValue('stuntdoubledown', value)}
            />

            <div className="section-divider">
              <Typography variant="h3">Specialty</Typography>
            </div>

            <SkillJudge
              title="Switch Up"
              value={currentAthlete?.skills?.stuntswitchup || 0}
              onChange={(value) => updateSkillValue('stuntswitchup', value)}
            />
            <SkillJudge
              title="Handspring Up"
              value={currentAthlete?.skills?.stunthandsprintup || 0}
              onChange={(value) => updateSkillValue('stunthandsprintup', value)}
            />
            <SkillJudge
              title="High-To-High"
              value={currentAthlete?.skills?.stunthightohigh || 0}
              onChange={(value) => updateSkillValue('stunthightohigh', value)}
            />
            <SkillJudge
              title="Rewind"
              value={currentAthlete?.skills?.stuntrewind || 0}
              onChange={(value) => updateSkillValue('stuntrewind', value)}
            />
            <CommentsBox
              value={stuntComments}
              onChange={(value) => handleCommentsChange(value, 'stunt')}
              onSave={() => saveSectionComments('stunt', stuntComments, currentAthlete?.id)}
              onBlur={() => handleCommentsBlur('stunt', currentAthlete?.id)}
              placeholder="Stunt comments..."
            />
          </div>
        );
      case 'basket':
        return (
          <div className="detail-content">
            <SkillJudge
              title="Straight Ride"
              value={currentAthlete?.skills?.basketstraight || 0}
              onChange={(value) => updateSkillValue('basketstraight', value)}
            />
            <SkillJudge
              title="Back Tuck"
              value={currentAthlete?.skills?.basketbacktuck || 0}
              onChange={(value) => updateSkillValue('basketbacktuck', value)}
            />
            <SkillJudge
              title="X Out"
              value={currentAthlete?.skills?.basketxout || 0}
              onChange={(value) => updateSkillValue('basketxout', value)}
            />
            <SkillJudge
              title="Full"
              value={currentAthlete?.skills?.basketfull || 0}
              onChange={(value) => updateSkillValue('basketfull', value)}
            />
            <CommentsBox
              value={basketComments}
              onChange={(value) => handleCommentsChange(value, 'basket')}
              onSave={() => saveSectionComments('basket', basketComments, currentAthlete?.id)}
              onBlur={() => handleCommentsBlur('basket', currentAthlete?.id)}
              placeholder="Basket comments..."
            />
          </div>
        );
      case 'dance':
        return (
          <div className="detail-content">
            <SkillJudge
              title="Motions"
              value={currentAthlete?.skills?.dancemotions || 0}
              onChange={(value) => updateSkillValue('dancemotions', value)}
            />
            <SkillJudge
              title="Performance"
              value={currentAthlete?.skills?.danceperformance || 0}
              onChange={(value) => updateSkillValue('danceperformance', value)}
            />
            <CommentsBox
              value={danceComments}
              onChange={(value) => handleCommentsChange(value, 'dance')}
              onSave={() => saveSectionComments('dance', danceComments, currentAthlete?.id)}
              onBlur={() => handleCommentsBlur('dance', currentAthlete?.id)}
              placeholder="Dance comments..."
            />
          </div>
        );
      case 'comments':
        return (
          <div className="detail-content">
            <Button
              variant="primary"
              fullWidth
              onClick={addNewComment}
              className="add-comment-button"
            >
              Add New Comment
            </Button>

            {commentList.map((comment, index) => (
              <CommentsBox
                key={index}
                value={comment}
                onChange={(value) => handleCommentChange(index, value)}
                onSave={() => handleCommentSave(index, currentAthlete?.id)}
                onBlur={() => handleCommentBlur(index, currentAthlete?.id)}
                placeholder={`Comment ${commentList.length - index}...`}
                compact={true}
              />
            ))}
          </div>
        );
      default:
        return (
          <div className="athlete-actions">
            <Button
              variant="primary"
              fullWidth
              onClick={() => setActiveSection('top-flexibility')}
            >
              Top Flexibility
            </Button>

            <Button
              variant="primary"
              fullWidth
              onClick={() => setActiveSection('jumps')}
            >
              Jumps
            </Button>

            <Button
              variant="primary"
              fullWidth
              onClick={() => setActiveSection('tumbling')}
            >
              Tumbling
            </Button>

            <Button
              variant="primary"
              fullWidth
              onClick={() => setActiveSection('stunt')}
            >
              Stunt
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={() => setActiveSection('basket')}
            >
              Basket
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={() => setActiveSection('dance')}
            >
              Dance
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={() => setActiveSection('comments')}
            >
              Comments
            </Button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div></div>
      //   <div className="new-athlete-page">
      //     <div className="new-athlete-top-bar">
      //       <div className="new-athlete-top-bar-left">
      //         <button className="new-athlete-top-bar-button" onClick={() => navigate(`/session/${sessionId}/athletes`)}>
      //           <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      //             <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
      //           </svg>
      //         </button>
      //       </div>
      //       <div className="new-athlete-top-bar-right">
      //         <button className="new-athlete-top-bar-button" onClick={handleRefresh}>
      //           <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      //             <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 3.81 7.58 3.81 12C3.81 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor"/>
      //           </svg>
      //         </button>
      //       </div>
      //     </div>
      //     <div className="new-athlete-content">
      //       {/* ... Copy loading state from AthletePage.tsx ... */}
      //     </div>
      //   </div>
    );
  }

  if (error || !currentAthlete) {
    return (
      <div className="new-athlete-page">
        <div className="new-athlete-top-bar">
          <div className="new-athlete-top-bar-left">
            <button className="new-athlete-top-bar-button" onClick={() => navigate(`/session/${sessionId}/athletes`)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className="new-athlete-top-bar-right">
            <button className="new-athlete-top-bar-button" onClick={handleRefresh}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 3.81 7.58 3.81 12C3.81 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor" />
              </svg>
            </button>
          </div>
        </div>
        <div className="new-athlete-content">
          <Container>
            <Card>
              <Typography variant="h2" align="center">Error</Typography>
              <div className="error-message">{error}</div>
            </Card>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="new-athlete-page">
      <div className="new-athlete-top-bar">
        <div className="new-athlete-top-bar-left">
          <button className="new-athlete-top-bar-button" onClick={() => navigate(`/session/${sessionId}/athletes`)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" />
            </svg>
          </button>
        </div>
        <div className="new-athlete-top-bar-center">
          <span className={showStickyName ? 'sticky-visible' : 'sticky-hidden'}>
            #{currentAthlete?.number} {currentAthlete?.name}
          </span>
        </div>
        <div className="new-athlete-top-bar-right">
          <button className="new-athlete-top-bar-button" onClick={handleRefresh}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 3.81 7.58 3.81 12C3.81 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>
      <div className="new-athlete-content">
        <Container>
          <Card>
            <div className="card-header">
              <Typography variant="h3">Athlete Details</Typography>
              <div className="card-header-right">
                {currentAthlete?.returningstatus != null &&
                  <Typography variant="h3" className="athlete-details-pill">
                    {currentAthlete?.returningstatus}
                  </Typography>
                }

                <Typography variant="h3" className="athlete-details-pill">
                  {currentAthlete?.position}
                </Typography>
              </div>
            </div>
            <div className="athlete-details-container">
              <div className="athlete-details-header">
                <div className="athlete-details-photo" onClick={() => currentAthlete?.imageUrl && setShowFullImage(true)}>
                  {(currentAthlete?.imageThumbnailUrl || currentAthlete?.imageUrl) ? (
                    <img
                      src={currentAthlete.imageThumbnailUrl ?? currentAthlete.imageUrl}
                      alt={currentAthlete.name}
                      onLoad={handleImageLoad}
                      style={{
                        opacity: imageLoaded ? 1 : 0,
                        transform: imageLoaded ? 'scale(1)' : 'scale(0.9)',
                        transition: 'opacity 0.4s ease'
                      }}
                    />
                  ) : (
                    <div className="placeholder-image">
                      <span>{currentAthlete?.name.charAt(0)}</span>
                    </div>
                  )}
                </div>

                <div className="athlete-details-info">
                  <div className="athlete-details-main">
                    <div className="athlete-details-num">
                      <Typography variant="h3">#{currentAthlete?.number}</Typography>
                    </div>
                    <div className="athlete-details-name">
                      <Typography variant="h3">{currentAthlete?.name}</Typography>
                    </div>
                  </div>
                  <div className="athlete-details-coach">
                    <Typography variant="body">Coach: {currentAthlete?.createdBy}</Typography>
                    {currentAthlete?.createdBy !== coachName && (
                      <Button
                        variant="secondary"
                        onClick={() => handleCoachChange({ target: { value: coachName } } as React.ChangeEvent<HTMLSelectElement>)}
                      >
                        + Assign To Me
                      </Button>
                    )}
                    {currentAthlete?.createdBy === coachName && previousCoach !== undefined && previousCoach !== coachName && (
                      <Button
                        variant="secondary"
                        onClick={handleUndoCoachChange}
                      >
                        Undo Assign to Me
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {activeSection !== 'main' && (
                <div className="athlete-details-section-header" onClick={() => setActiveSection('main')}>
                  <Button
                    variant="secondary"
                    onClick={() => setActiveSection('main')}
                  >
                    ↩
                  </Button>
                  <Typography variant="h3">
                    {activeSection === 'top-flexibility' && 'Top Flexibility'}
                    {activeSection === 'jumps' && 'Jumps'}
                    {activeSection === 'tumbling' && 'Tumbling'}
                    {activeSection === 'stunt' && 'Stunt'}
                    {activeSection === 'dance' && 'Dance'}
                    {activeSection === 'comments' && 'Comments'}
                  </Typography>
                </div>
              )}

              <div className="athlete-details-content">
                {renderDetailContent()}
              </div>

              {activeSection === 'main' ? (
                <div className="athlete-details-actions">
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={handleShowEditDialog}
                  >
                    Edit Athlete
                  </Button>

                  <Button
                    variant="danger"
                    fullWidth
                    onClick={handleShowDeleteDialog}
                  >
                    Delete Athlete
                  </Button>
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </Card>
        </Container>
      </div>

      {showDeleteDialog && (
        <div className="athlete-details-dialog-overlay">
          <div className="athlete-details-dialog">
            <Typography variant="h3">Delete Athlete</Typography>
            <Typography variant="body">
              Are you sure? This will delete the athlete.
            </Typography>
            <div className="athlete-details-dialog-actions">
              <Button
                variant="secondary"
                onClick={handleHideDeleteDialog}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAthlete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showFullImage && currentAthlete?.imageUrl && (
        <div className="athlete-details-fullimage-overlay" onClick={() => setShowFullImage(false)}>
          <div className="athlete-details-fullimage-container">
            <img
              src={currentAthlete.imageUrl}
              alt={currentAthlete.name}
              onLoad={handleFullImageLoad}
              style={{
                opacity: fullImageLoaded ? 1 : 0,
                transition: 'opacity 0.4s ease',
              }}
            />
            <Button
              variant="secondary"
              className="athlete-details-close-button"
              onClick={() => setShowFullImage(false)}
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {showEditDialog && (
        <div className="athlete-details-dialog">
          <div className="athlete-details-form">
            <Typography variant="h3">Edit Athlete</Typography>
            <div className="form-group">
              <Input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                label="Athlete Name"
                placeholder="Enter athlete name"
                required
              />
            </div>

            <div className="form-group">
              <Input
                type="tel"
                value={editForm.number}
                onChange={(e) => setEditForm({ ...editForm, number: e.target.value })}
                label="Athlete Number"
                placeholder="Enter athlete number"
                inputMode="numeric"
                pattern="[0-9]*"
                required
              />
            </div>

            <div className="form-group">
              <label className="input-label">Position</label>
              <div className="select-container">
                <select
                  value={editForm.position}
                  onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                  className="select-input"
                  required
                >
                  <option value="Unknown">Unknown</option>
                  <option value="Top">Top</option>
                  <option value="Base">Base</option>
                  <option value="Third">Third</option>
                  <option value="Multi">Multi</option>
                </select>
                <div className="select-arrow">▼</div>
              </div>
            </div>

            <div className="form-group">
              <label className="input-label">Status</label>
              <div className="select-container">
                <select
                  value={editForm.returningstatus}
                  onChange={(e) => setEditForm({ ...editForm, returningstatus: e.target.value })}
                  className="select-input"
                  required
                >
                  <option value="Unknown">Unknown</option>
                  <option value="New">New</option>
                  <option value="Returning">Returning</option>
                  <option value="Summer">Summer</option>
                </select>
                <div className="select-arrow">▼</div>
              </div>
            </div>

            <div className="form-group">
              <label className="input-label">Coach</label>
              <div className="select-container">
                <select
                  value={editForm.createdBy}
                  onChange={(e) => setEditForm({ ...editForm, createdBy: e.target.value })}
                  className="select-input"
                  required
                >
                  {<option key="-1" value={""}>No Coach</option>}
                  {coaches.map(coach => (
                    <option key={coach.id} value={coach.name}>
                      {coach.name}
                    </option>
                  ))}
                </select>
                <div className="select-arrow">▼</div>
              </div>
            </div>

            <div className="form-group">
              <PhotoUpload
                imageUrl={editForm.imageUrl || currentAthlete?.imageUrl}
                onImageUpload={({ imageUrl, imageThumbnailUrl }) => setEditForm({ ...editForm, imageUrl, imageThumbnailUrl })}
                onUploadingChange={setPhotoUploading}
                className="athlete-photo-upload"
                label="Athlete Picture"
                withThumbnail={true}
              />
            </div>

            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={handleHideEditDialog}
                disabled={false}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                onClick={handleEditAthlete}
                disabled={editing || photoUploading}
                fullWidth
              >
                {editing ? 'Saving...' : photoUploading ? 'Uploading...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <NewAthleteTabs
        athletes={athletes}
        currentAthleteId={athleteId}
        onAthleteSelect={(athlete) => {
          navigate(`/session/${sessionId}/athlete-page/${athlete.id}`);
        }}
      />
    </div>
  );
} 